import React from 'react';

// Namespaces
import * as ProposalNS from '../../Proposal';

// Utils
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';
import { formatMessageWithLocale } from '@/utils/i18n/i18n';
import { getItemDatesFromPath, getDateRangeFromQuickRange } from '@/utils/disclaimers/comparison';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import FormattedMessage from '@/utils/i18n/FormattedMessage';

// Components
import {
  AssetAllocationDisclaimer,
  HoldingsDisclaimer,
  LipperLeadersDisclaimer,
  PerformanceDisclaimer,
  RiskDisclaimer,
  FundataMERDisclaimer,
  FixedIncomeMetricsDisclaimer,
  CreditQualityDisclaimer,
  MaturityDisclaimer,
  FixedIncomeRegionAllocationDisclaimer,
  ShortHistoryPortfoliosDisclaimer,
  ReturnOnEquityDisclaimer,
  EquityStyleDisclaimer,
  ModifiedDurationDisclaimer,
  FixedIncomeStyleDisclaimer,
  FixedIncomeSectorsDisclaimer,
  InstrumentCustomDisclaimer,
  HypotheticalReturnDisclaimer,
  DrawdownDisclaimer,
  BetaDisclaimer,
  EquityOtherMetricsDisclaimer,
  BenchmarksDisclaimer,
  UpDownMonthsDisclaimer,
  CalculationBenchmarksDisclaimer,
  BMOGifMarketingTemplateDisclaimer,
  CustomProviderRiskRatingDisclaimer,
  YieldDisclaimer,
  YieldToMaturityDisclaimer,
  ESGOverviewDisclaimer,
  ESGImpliedTemperatureRiseDisclaimer,
  ESGCarbonIntensityDisclaimer,
  SinceInceptionDateDisclaimer,
  CommonInceptionDateDisclaimer,
  CustomDisclaimer,
  DefaultDisclaimer,
} from '@/views/components/disclaimers/comparison';

import DatesDisclaimer from '@/views/components/disclaimers/comparison/DatesDisclaimer';
import RiskProfileMayDifferDisclaimer from '@/views/components/disclaimers/comparison/RiskProfileMayDifferDisclaimer';

// Types
import BenchmarksBreakdownDisclaimer from '@/views/components/disclaimers/comparison/BenchmarksBreakdownDisclaimer';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import RollingIntervals from '@/types/rolling-intervals';
import HistoricalScenario from '@/types/historical-scenario';
import moment from 'moment';
import Nullable from '@/types/nullable';
import { TIME_PERIODS } from '@/utils/constants';

type Props = {
  disclaimerKey: string;
  items: ComparisonItem[];
  showItemNames?: boolean;
  locale?: Locale;
  proposal?: ProposalNS.Proposal;
  detailed?: boolean;
  dateRangeConfig?: {
    startDate: Nullable<Date>;
    endDate: Nullable<Date>;
    dateQuickRange: Nullable<TIME_PERIODS>;
  };
  applyRelevanceYearLimit: boolean;
  sectionKey?: string;

  // add this to an optional props sub object
  comparison?: Comparison;

  config?: {
    series?: number[][];
    interval?: RollingIntervals;
    customString?: LocalizedString;
    isHTML?: boolean;
    useDates?: boolean;
    useDetailedDates?: boolean;
    datesPath?: string;
    scenario?: HistoricalScenario;
  };
};

export default function ProposalDisclaimer(props: Props) {
  const { config, detailed, disclaimerKey, items, locale, proposal, showItemNames, dateRangeConfig, applyRelevanceYearLimit, sectionKey } = props;

  // Vague TODO - find a more scalable solution.
  switch (disclaimerKey) {
    case PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT: {
      return <AssetAllocationDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.DETAILED_ALLOCATIONS_AS_AT: {
      return <AssetAllocationDisclaimer items={items} locale={locale} showItemNames={showItemNames} detailed />;
    }

    case PROPOSAL_DISCLAIMERS.HOLDINGS_AS_AT: {
      return <HoldingsDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.YIELD_AS_AT: {
      return <YieldDisclaimer items={items} locale={locale} showItemNames={showItemNames} detailed />;
    }

    case PROPOSAL_DISCLAIMERS.LIPPER_AS_AT: {
      return <LipperLeadersDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.PERFORMANCE_PERIOD: {
      return <PerformanceDisclaimer items={items} locale={locale} showItemNames={showItemNames} detailed={detailed} />;
    }

    case PROPOSAL_DISCLAIMERS.RISK_PERIOD: {
      return <RiskDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.RISK_PROFILE_MAY_DIFFER: {
      return <RiskProfileMayDifferDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.BETA_DISCLAIMER: {
      return <BetaDisclaimer items={items} />;
    }

    case PROPOSAL_DISCLAIMERS.FUNDATA_MERS: {
      return <FundataMERDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.OTHER_FIXED_INCOME_METRICS: {
      return <FixedIncomeMetricsDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.CREDIT_QUALITY_AS_AT: {
      return <CreditQualityDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.MATURITY_AS_AT: {
      return <MaturityDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.FIXED_INCOME_REGION_AS_AT: {
      return <FixedIncomeRegionAllocationDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS: {
      if (!proposal || !proposal.shortHistory) return null;

      let startDate = dateRangeConfig?.startDate;
      let endDate = dateRangeConfig?.endDate;

      if (!startDate || !endDate) {
        if (dateRangeConfig?.dateQuickRange) {
          const dateRange = getDateRangeFromQuickRange(dateRangeConfig.dateQuickRange, undefined, undefined, items, true, applyRelevanceYearLimit);
          startDate = startDate || dateRange.startDate;
          endDate = endDate || dateRange.endDate;
        }
      }

      return <ShortHistoryPortfoliosDisclaimer items={items} locale={locale} startDate={startDate} endDate={endDate} />;
    }

    case PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER: {
      return <InstrumentCustomDisclaimer disclaimerTags={[DISCLAIMER_TAG.PROPOSAL]} items={items} locale={locale} short />;
    }

    case PROPOSAL_DISCLAIMERS.EQUITY_STYLE: {
      return <EquityStyleDisclaimer items={items} locale={locale} showItemNames={showItemNames} user={proposal?.appUser} />;
    }

    case PROPOSAL_DISCLAIMERS.RETURN_ON_EQUITY: {
      return <ReturnOnEquityDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.MODIFIED_DURATION: {
      return <ModifiedDurationDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.YIELD_TO_MATURITY: {
      return <YieldToMaturityDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.FIXED_INCOME_STYLE: {
      return <FixedIncomeStyleDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.FIXED_INCOME_SECTOR: {
      return <FixedIncomeSectorsDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }

    case PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE: {
      return <SinceInceptionDateDisclaimer disclaimerKey={disclaimerKey} items={proposal?.items} locale={locale} sectionKey={sectionKey} />;
    }

    case PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE: {
      return <CommonInceptionDateDisclaimer commonInception={proposal?.commonInception} locale={locale} />;
    }

    case PROPOSAL_DISCLAIMERS.HYPOTHETICAL_RETURN: {
      if (!config || !config.series || !config.interval) {
        return null;
      }
      const series: Array<number> = config.series.reduce((acc, serie) => acc.concat(serie), []);
      const lastSerie: Array<number> = series[series.length - 1] as unknown as Array<number>;
      const lastSerieDate: Date = new Date(lastSerie[0]);
      return (
        <HypotheticalReturnDisclaimer
          items={items}
          locale={locale}
          series={config.series}
          interval={config.interval}
          showItemNames={showItemNames}
          endDate={lastSerieDate}
        />
      );
    }
    case PROPOSAL_DISCLAIMERS.DRAWDOWN_ANALYSIS: {
      if (!config || !config.series || !config.interval) {
        return null;
      }
      return <DrawdownDisclaimer items={items} locale={locale} series={config.series} interval={config.interval} showItemNames={showItemNames} />;
    }
    case PROPOSAL_DISCLAIMERS.EQUITY_OTHER_METRICS: {
      return <EquityOtherMetricsDisclaimer items={items} locale={locale} showItemNames={showItemNames} user={proposal?.appUser} />;
    }
    case PROPOSAL_DISCLAIMERS.BENCHMARKS: {
      return <BenchmarksDisclaimer items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.BENCHMARKS_BREAKDOWN: {
      return (
        <BenchmarksBreakdownDisclaimer
          benchmarksBreakdownDisclaimers={proposal && proposal.template.benchmarksBreakdownDisclaimers}
          items={items}
          locale={locale}
        />
      );
    }
    case PROPOSAL_DISCLAIMERS.UP_DOWN_MONTHS: {
      return <UpDownMonthsDisclaimer items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.CALCULATION_BENCHMARKS: {
      return <CalculationBenchmarksDisclaimer proposal={proposal} items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.BMO_GIF_MARKETING_DISCLAIMER: {
      return <BMOGifMarketingTemplateDisclaimer items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.CUSTOM_PROVIDER_RISK_RATING: {
      return <CustomProviderRiskRatingDisclaimer items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.ESG_CARBON_INTENSITY: {
      return <ESGCarbonIntensityDisclaimer items={items} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.CUSTOM_ALLOCATIONS: {
      if (config?.datesPath == null) {
        return null;
      }

      return (
        <DatesDisclaimer
          globalPrefix={formatMessageWithLocale('Disclaimers.Allocations', locale)}
          itemPrefix={formatMessageWithLocale('Disclaimers.AsAt', locale)}
          itemDates={getItemDatesFromPath(items, config.datesPath, locale)}
          showItemNames={showItemNames}
          locale={locale}
        />
      );
    }
    case PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER: {
      if (config === undefined) {
        return null;
      }
      return <CustomDisclaimer config={config} items={items} locale={locale} showItemNames={showItemNames} />;
    }
    case PROPOSAL_DISCLAIMERS.ESG_OVERVIEW: {
      return <ESGOverviewDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }
    case PROPOSAL_DISCLAIMERS.ESG_IMPLIED_TEMPERATURE_RISE: {
      return <ESGImpliedTemperatureRiseDisclaimer items={items} locale={locale} showItemNames={showItemNames} />;
    }
    case PROPOSAL_DISCLAIMERS.HISTORICAL_SCENARIO_RETURN: {
      if (config?.scenario == null || config?.scenario.startDate == null || config?.scenario.endDate == null) {
        return null;
      }

      return (
        <span className="disclaimer" data-private data-dd-privacy="mask">
          <FormattedMessage
            id={`Disclaimers.${disclaimerKey}`}
            locale={locale}
            values={{
              startDate: moment.utc(config?.scenario.startDate).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
              endDate: moment.utc(config?.scenario.endDate).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
            }}
          />
        </span>
      );
    }
    case PROPOSAL_DISCLAIMERS.TAX_ANALYSIS: {
      return (
        <div className="disclaimer" role="note" data-private data-dd-privacy="mask">
          {formatMessageWithLocale('Comparison.TaxAnalysis.Disclaimer', locale)}
        </div>
      );
    }
    case PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER: {
      return <DefaultDisclaimer asterisk block disclaimerKey={disclaimerKey} locale={locale} />;
    }
    case PROPOSAL_DISCLAIMERS.SI_YOUNG_HOLDINGS: {
      return <DefaultDisclaimer asterisk block disclaimerKey={disclaimerKey} locale={locale} sectionKey={sectionKey} />;
    }
    default: {
      return <DefaultDisclaimer disclaimerKey={disclaimerKey} locale={locale} />;
    }
  }
}
